import "../SettingsPage/SettingsPage.css";
import { signal, useSignal } from "@preact/signals";
import { lazy, Suspense } from "preact/compat";
import type { SeedBibleState } from "../../managers/SeedBibleStateManager";
import type { ModalManager } from "../../managers/ModalManager";
import {
  CUSTOMIZATION_COLOR_GROUPS,
  CUSTOMIZATION_CONTRAST_PAIRS,
  CUSTOMIZATION_FONT_FIELDS,
  MIN_READABLE_CONTRAST_RATIO,
  buildCustomFontValue,
  getContrastRatio,
  getExtensionAvailability,
  getExtensionSettingDefault,
  getFontPresetsForField,
  type CustomizationsManager,
  type ExtensionAvailability,
} from "../../managers/CustomizationsManager";
import {
  DEFAULT_HIGHLIGHT_IDS,
  type BibleTheme,
  type ThemeColorKey,
  type ThemeFontFamilyKey,
} from "../../managers/ThemeManager";
import { useI18n } from "../../i18n/I18nManager";
import { MaterialIcon } from "../icons";
import { ExtensionSettingsForm } from "../ExtensionSettingsForm/ExtensionSettingsForm";
import type {
  ExtensionListEntry,
  ExtensionSettingDefinition,
  ExtensionSettingValue,
} from "../../managers/ExtensionManager";
import { Skeleton, SkeletonContainer } from "../Skeleton/Skeleton";
import { LazyColorPicker } from "../ColorPicker/LazyColorPicker";
import { normalizeHex } from "../ColorPicker/color";
import {
  ContextMenuItem,
  ContextMenuWithButton,
} from "../ContextMenu/ContextMenu";

// The picture editor pulls in `react-avatar-editor`, so it's only fetched on
// the "Upload logo" click rather than at boot, same as SettingsPage does.
const LogoCropModalContent = lazy(() =>
  import("../LogoCropModal/LogoCropModal").then((m) => ({
    default: m.LogoCropModalContent,
  }))
);

export const CUSTOMIZATION_EDIT_PANE_ID = "customization-edit-pane";

type CustomizationEditView = "edit" | "edit-variant" | "edit-extensions";

/**
 * Whether `foregroundKey` (e.g. `primaryFontColor`) has a known reading
 * pair (e.g. `primaryColor`) in `CUSTOMIZATION_CONTRAST_PAIRS`, and if so,
 * whether its resolved color falls short of `MIN_READABLE_CONTRAST_RATIO`
 * against that pair's background. `null` when the field has no such pair,
 * or its contrast is already sufficient.
 */
function getContrastWarning(
  foregroundKey: ThemeColorKey,
  resolvedTheme: BibleTheme
): { ratio: number; label: string } | null {
  const pair = CUSTOMIZATION_CONTRAST_PAIRS.find(
    (p) => p.foreground === foregroundKey
  );
  if (!pair) {
    return null;
  }
  const foreground = resolvedTheme.variables[pair.foreground];
  const background = resolvedTheme.variables[pair.background];
  if (!foreground || !background) {
    return null;
  }
  const ratio = getContrastRatio(foreground, background);
  if (ratio >= MIN_READABLE_CONTRAST_RATIO) {
    return null;
  }
  return { ratio, label: pair.label };
}

/** Small inline warning icon for a color row whose contrast is too low to read comfortably. */
function ContrastWarningIcon(props: { ratio: number; label: string }) {
  const { ratio, label } = props;
  const { t } = useI18n();
  const message = t("low-contrast-warning", {
    label,
    ratio: ratio.toFixed(1),
    minRatio: MIN_READABLE_CONTRAST_RATIO.toFixed(1),
    defaultValue:
      "{{label}}: contrast is only {{ratio}}:1 — aim for at least {{minRatio}}:1 so text stays readable.",
  });
  return (
    <span
      className="sb-theme-contrast-warning material-symbols-outlined"
      title={message}
      aria-label={message}
    >
      warning
    </span>
  );
}

/**
 * Which of the pane's three screens is showing.
 *
 * Module-level rather than component state because the pane's chrome — the
 * back button and the title — lives in the pane header, which the panes
 * manager renders outside this component (see `CustomizationEditPaneTitle`
 * and `CustomizationEditPaneLeading` below). There is only ever one
 * customization editor pane open at a time.
 */
const customizationEditView = signal<CustomizationEditView>("edit");

/** Pane header icon. Same glyph as the "Customize" entry in Settings. */
export function CustomizationEditPaneIcon() {
  return <MaterialIcon>palette</MaterialIcon>;
}

/** Pane header title: the customization/theme name, or the screen's own name. */
export function CustomizationEditPaneTitle(props: {
  customizations: CustomizationsManager;
}) {
  const { t } = useI18n();
  const record = props.customizations.editingCustomization.value;
  const view = customizationEditView.value;

  if (!record) {
    return <>{t("customize", { defaultValue: "Customize" })}</>;
  }
  if (view === "edit-extensions") {
    return <>{t("customization-extensions", { defaultValue: "Extensions" })}</>;
  }
  if (view === "edit-variant") {
    const variant = record.variants.find(
      (v) => v.id === props.customizations.editingVariantId.value
    );
    return <span dir="auto">{variant?.name ?? record.name}</span>;
  }
  return <span dir="auto">{record.name}</span>;
}

/** Pane header back button, shown on every screen except the main editor. */
export function CustomizationEditPaneLeading() {
  const { t } = useI18n();
  if (customizationEditView.value === "edit") {
    return null;
  }
  return (
    <button
      type="button"
      className="sb-settings-breadcrumbs-back"
      onClick={() => {
        customizationEditView.value = "edit";
      }}
      aria-label={t("back", { defaultValue: "Back" })}
      title={t("back", { defaultValue: "Back" })}
    >
      <span className="material-symbols-outlined">arrow_back</span>
    </button>
  );
}

const UNSAVED_CHANGES_CONFIRM_MODAL_ID =
  "customization-unsaved-changes-confirm";

/**
 * Confirmation body shown when the editor pane's close (X) button is
 * clicked while the draft has unsaved changes still pending — same
 * `sb-confirm-delete*` structure and button classes as the playlist
 * editor's "unsaved item" confirmation (`CreatePlaylistForm.tsx`), adapted
 * to this editor's own auto-save/discard actions instead of its "add
 * item" ones.
 */
function UnsavedChangesConfirmModalContent(props: {
  onKeepEditing: () => void;
  onDiscard: () => void;
  onSaveAndLeave: () => void;
}) {
  const { onKeepEditing, onDiscard, onSaveAndLeave } = props;
  const { t } = useI18n();

  return (
    <div className="sb-confirm-delete">
      <p className="sb-confirm-delete-message">
        {t("unsaved-changes-confirm-message", {
          defaultValue:
            "You have unsaved changes to this customization. What would you like to do?",
        })}
      </p>
      <div className="sb-confirm-delete-actions">
        <button
          type="button"
          className="sb-session-settings-cancel"
          onClick={onKeepEditing}
        >
          {t("keep-editing", { defaultValue: "Keep editing" })}
        </button>
        <button
          type="button"
          className="sb-session-settings-cancel"
          onClick={onDiscard}
        >
          {t("discard-changes", { defaultValue: "Discard changes" })}
        </button>
        <button
          type="button"
          className="sb-session-settings-end"
          onClick={onSaveAndLeave}
        >
          {t("save-and-leave", { defaultValue: "Save and leave" })}
        </button>
      </div>
    </div>
  );
}

/** Opens the unsaved-changes confirmation modal. */
function openUnsavedChangesConfirm(
  modals: ModalManager,
  onDiscard: () => void,
  onSaveAndLeave: () => void
) {
  modals.openModal({
    id: UNSAVED_CHANGES_CONFIRM_MODAL_ID,
    title: {
      key: "unsaved-changes-confirm-title",
      defaultValue: "Unsaved changes",
    },
    content: () => (
      <UnsavedChangesConfirmModalContent
        onKeepEditing={() =>
          modals.closeModal(UNSAVED_CHANGES_CONFIRM_MODAL_ID)
        }
        onDiscard={() => {
          modals.closeModal(UNSAVED_CHANGES_CONFIRM_MODAL_ID);
          onDiscard();
        }}
        onSaveAndLeave={() => {
          modals.closeModal(UNSAVED_CHANGES_CONFIRM_MODAL_ID);
          onSaveAndLeave();
        }}
      />
    ),
  });
}

/**
 * Opens (or, if already open, updates) the customization editor side pane on
 * a specific customization. Called from the customizations list in Settings.
 */
export function openCustomizationEditPane(
  state: SeedBibleState,
  id: string
): void {
  state.customizations.startEditing(id);
  customizationEditView.value = "edit";
  state.panes.openPane({
    id: CUSTOMIZATION_EDIT_PANE_ID,
    placement: "side",
    title: () => (
      <CustomizationEditPaneTitle customizations={state.customizations} />
    ),
    icon: () => <CustomizationEditPaneIcon />,
    leading: () => <CustomizationEditPaneLeading />,
    component: () => <CustomizationEditPane state={state} />,
    onClose: () => {
      state.customizations.stopEditing();
    },
    // Blocks the header's close (X) button while the draft has unsaved
    // edits, showing a confirmation instead of silently discarding or
    // (invisibly) auto-saving them. Returning `false` here leaves the pane
    // open; each modal action closes the pane itself afterward.
    confirmClose: () => {
      if (!state.customizations.hasUnsavedChanges.value) {
        return true;
      }
      openUnsavedChangesConfirm(
        state.modals,
        () => {
          state.customizations.discardEditingCustomization();
          state.panes.closePane(CUSTOMIZATION_EDIT_PANE_ID, "user");
        },
        () => {
          void (async () => {
            await state.customizations.saveEditingCustomization();
            state.panes.closePane(CUSTOMIZATION_EDIT_PANE_ID, "user");
          })();
        }
      );
      return false;
    },
  });
}

/**
 * Pane content for the customization editor. Switches between the main
 * editor, a single theme variant's editor, and the extensions screen. The
 * chrome around it — back button and title — is rendered by the pane header
 * (see the exports above), so this component renders only the body.
 */
export function CustomizationEditPane(props: { state: SeedBibleState }) {
  const { state } = props;
  const view = customizationEditView.value;

  if (view === "edit-extensions") {
    return <CustomizationEditExtensionsView state={state} />;
  }
  if (view === "edit-variant") {
    return <CustomizationEditVariantView state={state} />;
  }
  return <CustomizationEditMainView state={state} />;
}

function CustomizationEditMainView(props: { state: SeedBibleState }) {
  const { state } = props;
  const { customizations } = state;
  const { t } = useI18n();
  const confirmingDelete = useSignal(false);
  const isUploadingLogo = useSignal(false);

  const record = customizations.editingCustomization.value;

  const openVariant = (variantId: string) => {
    customizations.editingVariantId.value = variantId;
    customizationEditView.value = "edit-variant";
    // Select this variant as the live-previewed one (only takes effect if
    // this customization is the currently active one) so edits made in the
    // variant editor are visible immediately, rather than requiring the
    // user to separately go pick it from the theme gallery first.
    void customizations.selectActiveVariant(variantId);
  };

  const handleAddVariant = () => {
    const variant = customizations.addEditingVariant();
    if (variant) {
      openVariant(variant.id);
    }
  };

  const handleSave = async () => {
    await customizations.saveEditingCustomization();
    state.app.toast(
      t("customization-saved", { defaultValue: "Customization saved" })
    );
  };

  const handleUploadLogo = () => {
    const modalId = state.modals.openModal({
      title: { key: "upload-logo", defaultValue: "Upload logo" },
      content: () => (
        <Suspense
          fallback={
            <SkeletonContainer
              label={t("loading-picture-editor", {
                defaultValue: "Loading the picture editor…",
              })}
            >
              <Skeleton width="100%" height="16rem" radius="0.625rem" />
            </SkeletonContainer>
          }
        >
          <LogoCropModalContent
            onClose={() => state.modals.closeModal(modalId)}
            onUpload={async (file) => {
              isUploadingLogo.value = true;
              try {
                await customizations.uploadLogo(file);
              } catch (error) {
                console.error("Failed to upload logo.", error);
                throw error;
              } finally {
                isUploadingLogo.value = false;
              }
            }}
          />
        </Suspense>
      ),
    });
  };

  if (!record) {
    return (
      <div className="sb-settings-page">
        <section className="sb-settings-section">
          <div className="sb-settings-empty-state">
            <p>
              {t("customization-not-found", {
                defaultValue: "This customization could not be found.",
              })}
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="sb-settings-page">
      <section className="sb-settings-section">
        <div className="sb-settings-field-row">
          <label className="sb-settings-field-label">
            {t("customization-name", { defaultValue: "Name" })}
          </label>
          <input
            type="text"
            className="sb-settings-text-input"
            value={record.name}
            onChange={(event: Event) => {
              const target = event.currentTarget as HTMLInputElement;
              customizations.updateEditingName(target.value);
            }}
          />
        </div>

        <div className="sb-settings-field-row">
          <label className="sb-settings-field-label">
            {t("logo", { defaultValue: "Logo" })}
          </label>
          <div className="sb-customization-logo-row">
            {record.logoUrl ? (
              <img
                className="sb-customization-logo-preview"
                src={record.logoUrl}
                alt={t("logo", { defaultValue: "Logo" })}
              />
            ) : (
              <div
                className="sb-customization-logo-placeholder"
                aria-hidden="true"
              >
                <span className="material-symbols-outlined">image</span>
              </div>
            )}
            <button
              type="button"
              className="sb-settings-action-button"
              onClick={handleUploadLogo}
              disabled={isUploadingLogo.value}
            >
              {t("upload-logo", { defaultValue: "Upload logo" })}
            </button>
            {record.logoUrl && (
              <button
                type="button"
                className="sb-settings-action-button"
                onClick={() => void customizations.removeEditingLogo()}
                disabled={isUploadingLogo.value}
              >
                {t("remove-logo", { defaultValue: "Remove logo" })}
              </button>
            )}
          </div>
        </div>

        <section className="sb-settings-section">
          <h3 className="sb-settings-subheading">
            {t("variants", { defaultValue: "Themes" })}
          </h3>
          <ul className="sb-settings-list">
            {record.variants.map((variant) => (
              <li
                key={variant.id}
                className="sb-settings-nav-item sb-customization-row"
                onClick={() => openVariant(variant.id)}
              >
                <span className="sb-customization-swatches" aria-hidden="true">
                  <span
                    className="sb-customization-swatch"
                    style={{ background: variant.themes.primaryColor }}
                  />
                  <span
                    className="sb-customization-swatch"
                    style={{ background: variant.themes.secondaryColor }}
                  />
                  <span
                    className="sb-customization-swatch"
                    style={{ background: variant.themes.tertiaryColor }}
                  />
                </span>
                <span className="sb-settings-nav-label">{variant.name}</span>
                {variant.id === record.defaultVariantId && (
                  <span className="sb-customization-active-badge">
                    {t("default", { defaultValue: "Default" })}
                  </span>
                )}
                {variant.id !== record.defaultVariantId && (
                  <ContextMenuWithButton
                    buttonClassName="sb-extension-row-action-button"
                    aria-label={t("variant-options", {
                      defaultValue: "Theme options",
                    })}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        customizations.setEditingDefaultVariant(variant.id);
                      }}
                    >
                      <MaterialIcon className="sb-context-menu-item-icon">
                        star
                      </MaterialIcon>
                      <span>
                        {t("set-as-default-variant", {
                          defaultValue: "Set as default",
                        })}
                      </span>
                    </ContextMenuItem>
                  </ContextMenuWithButton>
                )}
                <span className="material-symbols-outlined rtl-mirror">
                  chevron_right
                </span>
              </li>
            ))}
          </ul>
          <div className="sb-settings-actions">
            <button
              type="button"
              className="sb-settings-action-button"
              onClick={handleAddVariant}
            >
              {t("add-variant", { defaultValue: "Add theme" })}
            </button>
          </div>
        </section>

        <button
          type="button"
          className="sb-settings-nav-item"
          onClick={() => {
            customizationEditView.value = "edit-extensions";
          }}
        >
          <span>
            {t("customization-extensions", { defaultValue: "Extensions" })}
          </span>
          <span className="material-symbols-outlined rtl-mirror">
            chevron_right
          </span>
        </button>

        <div className="sb-settings-actions">
          <button
            type="button"
            className="sb-settings-save-button"
            onClick={() => void handleSave()}
          >
            {t("save", { defaultValue: "Save" })}
          </button>

          <button
            type="button"
            className="sb-settings-action-button"
            onClick={() => {
              navigator.clipboard.writeText(
                customizations.getShareLink(record)
              );
              state.app.toast(
                t("customization-link-copied", {
                  defaultValue: "Customization link copied to clipboard",
                })
              );
            }}
          >
            {t("share", { defaultValue: "Share" })}
          </button>

          {confirmingDelete.value ? (
            <button
              type="button"
              className="sb-settings-action-button"
              onClick={() => {
                void customizations.remove(record.id);
                state.panes.closePane(CUSTOMIZATION_EDIT_PANE_ID);
              }}
            >
              {t("confirm-delete-customization", {
                defaultValue: "Confirm delete",
              })}
            </button>
          ) : (
            <button
              type="button"
              className="sb-settings-action-button"
              onClick={() => {
                confirmingDelete.value = true;
              }}
            >
              {t("delete-customization", { defaultValue: "Delete" })}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

/** What a newly overridden setting starts from when it declares no default. */
const EMPTY_SETTING_VALUES: Record<
  ExtensionSettingDefinition["type"],
  ExtensionSettingValue
> = { string: "", boolean: false, number: 0 };

function CustomizationEditExtensionsView(props: { state: SeedBibleState }) {
  const { state } = props;
  const { customizations, extensions } = state;
  const { t } = useI18n();

  const record = customizations.editingCustomization.value;

  if (!record) {
    return (
      <div className="sb-settings-page">
        <section className="sb-settings-section">
          <div className="sb-settings-empty-state">
            <p>
              {t("customization-not-found", {
                defaultValue: "This customization could not be found.",
              })}
            </p>
          </div>
        </section>
      </div>
    );
  }

  const installableExtensions = extensions.extensions.value.filter(
    (entry) => entry.extension !== null
  );

  const handleConfigureDefaults = (entry: ExtensionListEntry) => {
    const settings = entry.extension?.meta.settings ?? {};
    // The customization being edited, not the one the viewer currently has
    // active — those are only the same customization some of the time, and the
    // defaults written here belong to the draft.
    const draftDefault = (key: string) =>
      getExtensionSettingDefault(
        customizations.editingCustomization.value,
        entry.id,
        key
      );
    state.modals.openModal({
      title: {
        key: "extension-settings-defaults-title",
        defaultValue: "{{name}} defaults",
        options: {
          name:
            // eslint-disable-next-line seed-bible-i18n/translation-missing-keys
            t("title", { ns: entry.id, defaultValue: entry.id }),
        },
      },
      content: () => (
        <ExtensionSettingsForm
          extensionId={entry.id}
          settings={settings}
          getValue={draftDefault}
          // A Customization default overrides only the extension's own default.
          getDefault={(key) => settings[key]?.default}
          onChange={(key, value) =>
            customizations.setEditingExtensionSettingDefault(
              entry.id,
              key,
              value
            )
          }
          overriding={{
            isOverridden: (key) => draftDefault(key) !== undefined,
            onOverrideChange: (key, overridden) => {
              const definition = settings[key];
              if (!overridden || !definition) {
                customizations.clearEditingExtensionSettingDefault(
                  entry.id,
                  key
                );
                return;
              }
              // Storing a value as soon as the box is ticked keeps the record
              // and the checkbox saying the same thing; a setting that declares
              // no default starts from its type's empty value.
              customizations.setEditingExtensionSettingDefault(
                entry.id,
                key,
                definition.default ?? EMPTY_SETTING_VALUES[definition.type]
              );
            },
          }}
          t={t}
        />
      ),
    });
  };

  return (
    <div className="sb-settings-page">
      <section className="sb-settings-section">
        <p className="sb-settings-field-description">
          {t("customization-extensions-description", {
            defaultValue:
              "Choose how each extension behaves for anyone using this customization: available to install themselves, installed automatically with no prompt, or hidden from the extensions list entirely.",
          })}
        </p>
        {installableExtensions.length === 0 ? (
          <div className="sb-settings-empty-state">
            <p>
              {t("no-extensions-available", {
                defaultValue: "No extensions available.",
              })}
            </p>
          </div>
        ) : (
          installableExtensions.map((entry) => (
            <div className="sb-settings-field-row" key={entry.id}>
              <label
                className="sb-settings-field-label"
                htmlFor={`sb-customization-extension-${entry.id}`}
              >
                {
                  // eslint-disable-next-line seed-bible-i18n/translation-missing-keys
                  t("title", { ns: entry.id, defaultValue: entry.id })
                }
              </label>
              <select
                id={`sb-customization-extension-${entry.id}`}
                className="sb-settings-language-select"
                value={getExtensionAvailability(record, entry.id)}
                onChange={(event: Event) => {
                  const target = event.currentTarget as HTMLSelectElement;
                  customizations.setEditingExtensionAvailability(
                    entry.id,
                    target.value as ExtensionAvailability
                  );
                }}
              >
                <option value="available">
                  {t("extension-availability-available", {
                    defaultValue: "Available",
                  })}
                </option>
                <option value="auto-installed">
                  {t("extension-availability-auto-installed", {
                    defaultValue: "Auto-installed",
                  })}
                </option>
                <option value="hidden">
                  {t("extension-availability-hidden", {
                    defaultValue: "Hidden",
                  })}
                </option>
              </select>
              {entry.extension?.meta.settings &&
                Object.keys(entry.extension.meta.settings).length > 0 && (
                  <button
                    type="button"
                    className="sb-extension-row-action-button"
                    onClick={() => handleConfigureDefaults(entry)}
                    aria-label={t("configure-extension-defaults", {
                      defaultValue: "Configure defaults",
                    })}
                    title={t("configure-extension-defaults", {
                      defaultValue: "Configure defaults",
                    })}
                  >
                    <span className="material-symbols-outlined">tune</span>
                  </button>
                )}
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function CustomizationEditVariantView(props: { state: SeedBibleState }) {
  const { state } = props;
  const { customizations, theme } = state;
  const { t } = useI18n();
  const confirmingDelete = useSignal(false);

  const record = customizations.editingCustomization.value;
  const variant = record?.variants.find(
    (v) => v.id === customizations.editingVariantId.value
  );

  const handleSave = async () => {
    await customizations.saveEditingCustomization();
    state.app.toast(
      t("customization-saved", { defaultValue: "Customization saved" })
    );
  };

  if (!record || !variant) {
    return (
      <div className="sb-settings-page">
        <section className="sb-settings-section">
          <div className="sb-settings-empty-state">
            <p>
              {t("variant-not-found", {
                defaultValue: "This theme could not be found.",
              })}
            </p>
          </div>
        </section>
      </div>
    );
  }

  const isDefault = variant.id === record.defaultVariantId;
  const canDelete = record.variants.length > 1;
  const resolvedTheme = customizations.resolveEditingVariantTheme(variant);

  return (
    <div className="sb-settings-page">
      <section className="sb-settings-section">
        <div className="sb-settings-field-row">
          <label className="sb-settings-field-label">
            {t("variant-name", { defaultValue: "Name" })}
          </label>
          <input
            type="text"
            className="sb-settings-text-input"
            value={variant.name}
            onChange={(event: Event) => {
              const target = event.currentTarget as HTMLInputElement;
              customizations.renameEditingVariant(variant.id, target.value);
            }}
          />
        </div>

        <div className="sb-settings-field-row">
          <label
            className="sb-settings-field-label"
            htmlFor="sb-customization-variant-base-theme"
          >
            {t("base-theme", { defaultValue: "Base theme" })}
          </label>
          <select
            id="sb-customization-variant-base-theme"
            className="sb-settings-language-select"
            value=""
            onChange={(event: Event) => {
              const target = event.currentTarget as HTMLSelectElement;
              if (target.value) {
                customizations.applyPresetToEditingVariant(
                  variant.id,
                  target.value
                );
                target.value = "";
              }
            }}
          >
            <option value="">
              {t("select-base-theme", {
                defaultValue: "Base this theme on…",
              })}
            </option>
            {theme.themes.value.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.name}
              </option>
            ))}
          </select>
          <p className="sb-settings-field-description">
            {t("base-theme-description", {
              defaultValue:
                "Changes which preset this theme falls back to for anything you haven't customized. Your own edits are kept.",
            })}
          </p>
        </div>

        {CUSTOMIZATION_COLOR_GROUPS.map((group) => (
          <div key={group.id} className="sb-theme-colors-group">
            <h3 className="sb-settings-subheading">{group.title}</h3>
            <ul className="sb-theme-colors-list">
              {group.fields.map((field) => {
                const value = resolvedTheme.variables[field.key] ?? "";
                const isOverridden = variant.themes[field.key] !== undefined;
                const label = t(`customization-${field.key}`, {
                  defaultValue: field.label,
                });
                const contrastWarning = getContrastWarning(
                  field.key,
                  resolvedTheme
                );
                return (
                  <li key={field.key} className="sb-theme-color-row">
                    <div className="sb-theme-color-row-main">
                      <span className="sb-theme-color-label-row">
                        <span className="sb-theme-color-label">{label}</span>
                        {contrastWarning && (
                          <ContrastWarningIcon
                            ratio={contrastWarning.ratio}
                            label={contrastWarning.label}
                          />
                        )}
                      </span>
                      <span className="sb-theme-color-value">
                        {value || "—"}
                      </span>
                    </div>
                    <div className="sb-theme-color-row-controls">
                      <LazyColorPicker
                        value={normalizeHex(value)}
                        className="sb-theme-color-input"
                        ariaLabel={label}
                        onChange={(color) => {
                          customizations.setEditingVariantColor(
                            variant.id,
                            field.key,
                            color
                          );
                        }}
                        onPreview={(color) => {
                          customizations.previewEditingVariantColor(
                            variant.id,
                            field.key,
                            color
                          );
                        }}
                        onCancel={() => {
                          customizations.clearPreviewEditingVariantColor(
                            variant.id,
                            field.key
                          );
                        }}
                      />
                      {isOverridden && (
                        <button
                          type="button"
                          className="sb-theme-color-reset"
                          title={t("reset-to-base-theme", {
                            defaultValue: "Reset to base theme",
                          })}
                          aria-label={`Reset ${label}`}
                          onClick={() =>
                            customizations.resetEditingVariantField(
                              variant.id,
                              field.key
                            )
                          }
                        >
                          <span className="material-symbols-outlined">
                            restart_alt
                          </span>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="sb-theme-colors-group">
          <h3 className="sb-settings-subheading">
            {t("customization-fonts", { defaultValue: "Fonts" })}
          </h3>
          {CUSTOMIZATION_FONT_FIELDS.map((field) => (
            <CustomizationFontFieldRow
              key={field.key}
              variantId={variant.id}
              fieldKey={field.key}
              value={resolvedTheme.variables[field.key] ?? ""}
              isOverridden={variant.themes[field.key] !== undefined}
              label={t(`customization-${field.key}`, {
                defaultValue: field.label,
              })}
              customizations={customizations}
            />
          ))}
        </div>

        <div className="sb-theme-colors-group">
          <h3 className="sb-settings-subheading">
            {t("highlight-colors", { defaultValue: "Highlight colors" })}
          </h3>
          <ul className="sb-theme-colors-list">
            {DEFAULT_HIGHLIGHT_IDS.map((id) => {
              const effective = resolvedTheme.highlightColors[id];
              const bg = effective?.color ?? "";
              const fg = effective?.fontColor ?? "";
              const isOverridden = variant.highlightColors[id] !== undefined;
              const label = id.charAt(0).toUpperCase() + id.slice(1);
              const contrastRatio = bg && fg ? getContrastRatio(fg, bg) : null;
              const hasLowContrast =
                contrastRatio !== null &&
                contrastRatio < MIN_READABLE_CONTRAST_RATIO;
              return (
                <li key={id} className="sb-theme-color-row">
                  <div className="sb-theme-color-row-main">
                    <span className="sb-theme-color-label-row">
                      <span
                        className="sb-highlight-preview-pill"
                        style={{ background: bg, color: fg }}
                        aria-hidden="true"
                      >
                        {label}
                      </span>
                      {hasLowContrast && (
                        <ContrastWarningIcon
                          ratio={contrastRatio}
                          label={t("highlight-text-on-background", {
                            label,
                            defaultValue: "{{label}} text on its background",
                          })}
                        />
                      )}
                    </span>
                    <span className="sb-theme-color-value">{bg || "—"}</span>
                  </div>
                  <div className="sb-theme-color-row-controls">
                    <LazyColorPicker
                      value={normalizeHex(bg)}
                      className="sb-theme-color-input"
                      ariaLabel={t("id_highlight-background-color", { id })}
                      onChange={(color) => {
                        customizations.setEditingVariantHighlightColor(
                          variant.id,
                          id,
                          { color }
                        );
                      }}
                      onPreview={(color) => {
                        customizations.previewEditingVariantHighlightColor(
                          variant.id,
                          id,
                          { color }
                        );
                      }}
                      onCancel={() => {
                        customizations.clearPreviewEditingVariantHighlightField(
                          variant.id,
                          id,
                          "color"
                        );
                      }}
                    />
                    <LazyColorPicker
                      value={normalizeHex(fg)}
                      className="sb-theme-color-input"
                      ariaLabel={t("id_highlight-text-color", { id })}
                      onChange={(color) => {
                        customizations.setEditingVariantHighlightColor(
                          variant.id,
                          id,
                          { fontColor: color }
                        );
                      }}
                      onPreview={(color) => {
                        customizations.previewEditingVariantHighlightColor(
                          variant.id,
                          id,
                          { fontColor: color }
                        );
                      }}
                      onCancel={() => {
                        customizations.clearPreviewEditingVariantHighlightField(
                          variant.id,
                          id,
                          "fontColor"
                        );
                      }}
                    />
                    {isOverridden && (
                      <button
                        type="button"
                        className="sb-theme-color-reset"
                        title={t("reset-to-base-theme", {
                          defaultValue: "Reset to base theme",
                        })}
                        aria-label={`Reset ${label}`}
                        onClick={() =>
                          customizations.resetEditingVariantHighlightColor(
                            variant.id,
                            id
                          )
                        }
                      >
                        <span className="material-symbols-outlined">
                          restart_alt
                        </span>
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="sb-settings-actions">
          <button
            type="button"
            className="sb-settings-save-button"
            onClick={() => void handleSave()}
          >
            {t("save", { defaultValue: "Save" })}
          </button>

          {!isDefault && (
            <button
              type="button"
              className="sb-settings-action-button"
              onClick={() =>
                customizations.setEditingDefaultVariant(variant.id)
              }
            >
              {t("set-as-default-variant", { defaultValue: "Set as default" })}
            </button>
          )}

          {canDelete &&
            (confirmingDelete.value ? (
              <button
                type="button"
                className="sb-settings-action-button"
                onClick={() => {
                  customizations.removeEditingVariant(variant.id);
                  customizationEditView.value = "edit";
                }}
              >
                {t("confirm-delete-variant", {
                  defaultValue: "Confirm delete",
                })}
              </button>
            ) : (
              <button
                type="button"
                className="sb-settings-action-button"
                onClick={() => {
                  confirmingDelete.value = true;
                }}
              >
                {t("delete-variant", { defaultValue: "Delete" })}
              </button>
            ))}
        </div>
      </section>
    </div>
  );
}

/**
 * One font-family row in a customization variant's Fonts section. Custom
 * mode is tracked as its own local signal, separate from the stored value —
 * picking "Custom…" stores an empty value until a name is typed, and an
 * empty value is also what "nothing selected, use Default" looks like
 * (`CustomizationEditVariantView`'s field lookup falls back to the Default
 * preset for it). Without a separate "the user is actively in custom mode"
 * flag, storing that empty value would immediately read back as "nothing
 * selected" and snap the row back to Default, hiding the name field before
 * anything could be typed into it.
 */
function CustomizationFontFieldRow(props: {
  variantId: string;
  fieldKey: ThemeFontFamilyKey;
  value: string;
  isOverridden: boolean;
  label: string;
  customizations: CustomizationsManager;
}) {
  const { variantId, fieldKey, value, isOverridden, label, customizations } =
    props;
  const { t } = useI18n();
  const forcedCustom = useSignal(false);

  const fieldPresets = getFontPresetsForField(fieldKey);
  const matchedPreset = value
    ? fieldPresets.find((p) => p.value === value)
    : fieldPresets[0];
  // A stored value that doesn't match any preset is a real custom font
  // (e.g. reopening an editor that already has one saved) — show custom
  // mode for it even before the user has touched the select this session.
  const isCustom = forcedCustom.value || (!!value && !matchedPreset);
  const preset = isCustom ? undefined : matchedPreset;
  const customName = value.split(",")[0]?.trim() ?? "";

  return (
    <div className="sb-settings-field-row">
      <label
        className="sb-settings-field-label"
        htmlFor={`sb-customization-font-${fieldKey}`}
      >
        {label}
      </label>
      <div className="sb-settings-field-row-controls">
        <select
          id={`sb-customization-font-${fieldKey}`}
          className="sb-settings-language-select"
          value={preset ? preset.name : "__custom__"}
          onChange={(event: Event) => {
            const target = event.currentTarget as HTMLSelectElement;
            if (target.value === "__custom__") {
              forcedCustom.value = true;
              return;
            }
            forcedCustom.value = false;
            const nextPreset = fieldPresets.find(
              (p) => p.name === target.value
            );
            if (nextPreset) {
              customizations.setEditingVariantFont(
                variantId,
                fieldKey,
                nextPreset.value
              );
            }
          }}
        >
          {fieldPresets.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name === "Default"
                ? t("default", { defaultValue: "Default" })
                : p.name}
            </option>
          ))}
          <option value="__custom__">
            {t("custom-font-option", { defaultValue: "Custom…" })}
          </option>
        </select>
        {isCustom && (
          <input
            type="text"
            className="sb-settings-text-input"
            placeholder={t("custom-font-name-placeholder", {
              defaultValue: "Google Font name",
            })}
            value={customName}
            onInput={(event: Event) => {
              const target = event.currentTarget as HTMLInputElement;
              customizations.setEditingVariantFont(
                variantId,
                fieldKey,
                buildCustomFontValue(target.value)
              );
            }}
          />
        )}
        {isOverridden && (
          <button
            type="button"
            className="sb-theme-color-reset"
            title={t("reset-to-base-theme", {
              defaultValue: "Reset to base theme",
            })}
            aria-label={`Reset ${label}`}
            onClick={() => {
              forcedCustom.value = false;
              customizations.resetEditingVariantField(variantId, fieldKey);
            }}
          >
            <span className="material-symbols-outlined">restart_alt</span>
          </button>
        )}
      </div>
    </div>
  );
}
